import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { AuthLayout } from '../layouts/AuthLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RouteErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import {
  DashboardError,
  OrdersError,
  ContentOrdersError,
  WalletError,
  ProfileError,
  HelpCenterError,
  PackagesError,
  BrowseSitesError,
  NotificationsError,
  SubscriptionsError,
} from '@/components/ErrorBoundary/ModuleErrorBoundary';
import { lazy, Suspense } from 'react';
import { PageLoading } from '@/components/ui/LoadingComponents';
import {
  dashboardLoader,
  browseSitesLoader,
  packagesLoader,
  ordersLoader,
  contentOrdersLoader,
  subscriptionsLoader,
  walletLoader,
  profileLoader,
  notificationsLoader,
  helpCenterLoader,
  loginLoader,
  registerLoader,
  forgotPasswordLoader,
  resetPasswordLoader,
} from './loaders';
import { loginAction, registerAction, forgotPasswordAction, resetPasswordAction } from './actions';

// Lazy load all pages
const LoginPage = lazy(() =>
  import('@/modules/auth/pages/LoginPage').then((m) => ({
    default: m.LoginPage,
  }))
);
const RegisterPage = lazy(() =>
  import('@/modules/auth/pages/RegisterPage').then((m) => ({
    default: m.RegisterPage,
  }))
);
const ForgotPasswordPage = lazy(() =>
  import('@/modules/auth/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const VerifyEmailPage = lazy(() =>
  import('@/modules/auth/pages/VerifyEmailPage').then((m) => ({
    default: m.VerifyEmailPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import('@/modules/auth/pages/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const SSOCallbackPage = lazy(() =>
  import('@/modules/auth/pages/SSOCallbackPage').then((m) => ({
    default: m.SSOCallbackPage,
  }))
);

const DashboardPage = lazy(() =>
  import('@/modules/dashboard/pages/DashboardPage').then((m) => ({
    default: m.Dashboard,
  }))
);
const BrowseSitesPage = lazy(() =>
  import('@/components/browse-sites/BrowseSitesPage').then((m) => ({
    default: m.BrowseSitesPage,
  }))
);
const PackagesPage = lazy(() =>
  import('@/modules/packages/pages/PackagesPage').then((m) => ({
    default: m.PackagesPage,
  }))
);
const OrdersPage = lazy(() =>
  import('@/modules/orders/pages/OrdersPage').then((m) => ({
    default: m.OrdersPage,
  }))
);
const ContentOrdersPage = lazy(() =>
  import('@/components/content-orders/ContentOrdersPage').then((m) => ({
    default: m.ContentOrdersPage,
  }))
);
const SubscriptionsPage = lazy(() =>
  import('@/modules/subscriptions/pages/SubscriptionsPage').then((m) => ({
    default: m.SubscriptionsPage,
  }))
);
const WalletPage = lazy(() =>
  import('@/modules/wallet/pages/WalletPage').then((m) => ({
    default: m.WalletPage,
  }))
);
const ProfilePage = lazy(() =>
  import('@/modules/profile/pages/ProfilePage').then((m) => ({
    default: m.ProfilePage,
  }))
);
const NotificationsPage = lazy(() =>
  import('@/modules/notifications/pages/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  }))
);

const HelpCenterPage = lazy(() =>
  import('@/modules/help-center/pages/HelpCenterPage').then((m) => ({
    default: m.HelpCenterPage,
  }))
);
// Help Articles + FAQs — dedicated modules backed by the WP theme's
// db/v1 REST namespace. Voting + search + sort + pagination.
const HelpArticlesListPage = lazy(
  () => import('@/modules/help-articles/pages/HelpArticlesListPage')
);
const HelpArticleDetailPage = lazy(
  () => import('@/modules/help-articles/pages/HelpArticleDetailPage')
);
const FaqsPage = lazy(() => import('@/modules/faqs/pages/FaqsPage'));
const DepositReturnPage = lazy(() =>
  import('@/modules/wallet/pages/DepositReturnPage').then((m) => ({
    default: m.DepositReturnPage,
  }))
);
const NotFoundPage = lazy(() => import('./errors/NotFoundPage'));

export const router = createBrowserRouter([
  // Auth routes
  {
    path: '/',
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'login',
        loader: loginLoader,
        action: loginAction,
        element: (
          <Suspense fallback={<PageLoading />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: 'register',
        loader: registerLoader,
        action: registerAction,
        element: (
          <Suspense fallback={<PageLoading />}>
            <RegisterPage />
          </Suspense>
        ),
      },
      {
        path: 'forgot-password',
        loader: forgotPasswordLoader,
        action: forgotPasswordAction,
        element: (
          <Suspense fallback={<PageLoading />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: 'reset-password',
        loader: resetPasswordLoader,
        action: resetPasswordAction,
        element: (
          <Suspense fallback={<PageLoading />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: 'verify-email',
        element: (
          <Suspense fallback={<PageLoading />}>
            <VerifyEmailPage />
          </Suspense>
        ),
      },
      {
        path: 'auth/callback',
        element: (
          <Suspense fallback={<PageLoading />}>
            <SSOCallbackPage />
          </Suspense>
        ),
      },
    ],
  },
  // App routes (protected)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        loader: dashboardLoader,
        errorElement: <DashboardError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <DashboardPage onNavigate={() => {}} />
          </Suspense>
        ),
      },
      {
        path: 'browse-sites',
        loader: browseSitesLoader,
        errorElement: <BrowseSitesError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <BrowseSitesPage />
          </Suspense>
        ),
      },
      {
        path: 'packages',
        loader: packagesLoader,
        errorElement: <PackagesError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <PackagesPage />
          </Suspense>
        ),
      },
      {
        path: 'orders',
        loader: ordersLoader,
        errorElement: <OrdersError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <OrdersPage />
          </Suspense>
        ),
      },
      {
        path: 'content-orders',
        loader: contentOrdersLoader,
        errorElement: <ContentOrdersError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <ContentOrdersPage />
          </Suspense>
        ),
      },
      {
        path: 'subscriptions',
        loader: subscriptionsLoader,
        errorElement: <SubscriptionsError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <SubscriptionsPage />
          </Suspense>
        ),
      },
      {
        path: 'wallet',
        loader: walletLoader,
        errorElement: <WalletError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <WalletPage />
          </Suspense>
        ),
      },
      {
        // Airwallex hosted-checkout return URL. The embedded Drop-in
        // flow doesn't need this; it's used only when the user takes a
        // redirect fallback (`POST /payments/deposit/hosted`).
        path: 'wallet/deposit/return',
        element: (
          <Suspense fallback={<PageLoading />}>
            <DepositReturnPage />
          </Suspense>
        ),
      },
      {
        path: 'profile',
        loader: profileLoader,
        errorElement: <ProfileError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <ProfilePage />
          </Suspense>
        ),
      },
      {
        path: 'notifications',
        loader: notificationsLoader,
        errorElement: <NotificationsError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <NotificationsPage />
          </Suspense>
        ),
      },
      {
        path: 'help-center',
        loader: helpCenterLoader,
        errorElement: <HelpCenterError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <HelpCenterPage onArticleView={() => {}} />
          </Suspense>
        ),
      },
      {
        path: 'help-articles',
        errorElement: <HelpCenterError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <HelpArticlesListPage />
          </Suspense>
        ),
      },
      {
        path: 'help-articles/:slug',
        errorElement: <HelpCenterError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <HelpArticleDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'faqs',
        errorElement: <HelpCenterError />,
        element: (
          <Suspense fallback={<PageLoading />}>
            <FaqsPage />
          </Suspense>
        ),
      },
    ],
  },
  // Catch-all 404 — renders outside both layouts (paints its own background)
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoading />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);
